import { flow } from 'mobx';

import iconSearch from '@/assets/icons/search-off-icn-sm.svg';
import { LangID } from '@/constants/lang';
import { localStorageKeys } from '@/constants/localStorage';
import { query } from '@/constants/query';
import { serviceTypes } from '@/constants/services';
import i18n, { changeLanguage, init } from '@/locale/i18n';
// import { createContract } from '@/services/inpage';
import { AppError } from '@/services/rest/api/appError';
import { ServiceBase, ServiceLocalize } from '@/services/rest/base';
import { ServiceTime } from '@/services/time';
import { SettingsResponse, SyncReponse } from '@/types/api';
import { ServicesMixin } from '@/utils/servicesMixin';

import AppStore from './AppStore';
import BadgeStore from './BadgeStore';
import ErrorHandlerStore from './ErrorHandlerStore';
import MenuStore from './MenuStore';
import MissionsStore from './MissionsStore';
import ProfileStore from './ProfileStore';
import QueryStore from './QueryStore';
import WalletStore from './WalletStore';

import { toastManager } from '.';

@ServicesMixin([
  serviceTypes.REST,
  serviceTypes.QUERY,
  serviceTypes.TIME,
  serviceTypes.LOCALIZE,
])
export default class RootStore {
  readonly api: ServiceBase;
  readonly query: QueryStore;
  readonly time: ServiceTime;
  readonly localize: ServiceLocalize;

  readonly errorHandler = new ErrorHandlerStore(this);
  readonly menu = new MenuStore();
  readonly profile = new ProfileStore();
  readonly badges = new BadgeStore();
  readonly missions = new MissionsStore();
  readonly wallet = new WalletStore();

  readonly app = new AppStore({
    profileStore: this.profile,
    badgesStore: this.badges,
    missionsStore: this.missions,
  });

  constructor(
    services?: [ServiceBase, QueryStore, ServiceTime, ServiceLocalize],
  ) {
    const [apiService, queryService, timeService, localizeService] = services!;

    this.api = apiService;
    this.query = queryService;
    this.time = timeService;
    this.localize = localizeService;
  }

  @flow.bound *init(params: { token: string; lang: LangID }) {
    yield init();
    yield this.loadLang(params.lang);

    try {
      yield this.auth(params.token);

      const { hash } = yield this.getSettingsHash();
      localStorage.setItem(localStorageKeys.hash, hash);

      const settings: SettingsResponse = yield this.getSettings();
      const { player }: SyncReponse = yield this.sync();

      this.app.init();
      this.profile.init({ player, settings });
      this.badges.init({ settings, userBadges: player.badges });
      this.missions.init({
        missions: settings.mission,
        userMissions: player.missions,
      });
    } catch (error: unknown) {
      this.handleError(error as Error);
    }
  }

  @flow.bound *loadLang(lang = i18n.language as LangID) {
    try {
      const response: Record<string, string> = yield this.getLang(lang);
      yield changeLanguage(lang, async () => response);
      this.app.setLang(lang);
    } catch (e) {
      console.error(e);
      toastManager.error(
        `Unable to load ${lang} language, language set to default ${this.app.lang}`,
        '',
        5000,
      );
    }
  }

  private getLang(lang: LangID) {
    return this.query.withQuery(query.localize, () =>
      this.localize.getLang(lang),
    );
  }

  private auth(token: string) {
    this.api.setToken(token);
    return this.query.withQuery(
      query.auth,
      this.api.refreshToken.bind(this.api),
    );
  }

  private sync() {
    return this.query.withQuery(query.sync, this.api.sync.bind(this.api));
  }

  private getSettings() {
    return this.query.withQuery(query.settings, () => this.api.getSettings());
  }

  private getSettingsHash() {
    return this.query.withQuery(query.settingsHash, () =>
      this.api.getSettingsHash(),
    );
  }

  handleError(error: Error) {
    const isAppError = error instanceof AppError;
    if (isAppError) {
      toastManager.error(error.body.detail, iconSearch);
    }

    if (!isAppError && error.message) {
      toastManager.error(error.message);
    } else {
      toastManager.error('An error has occurred during execution');
    }

    throw error;
  }
}
